import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { StepHelpers } from "@shared/components/Stepper";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  FileCheck,
  Lock,
  LucideIcon,
  Mail,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApproveGenerateBill,
  useBulkBillData,
  useUpdateConsumerSelection,
} from "../../hooks";

interface CheckboxItem {
  key: keyof VerificationChecks;
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
}

interface VerificationChecks {
  billingAccuracy: boolean;
  dataReviewed: boolean;
  approveDistribution: boolean;
  completeProcess: boolean;
  dataConsent: boolean;
  calculationConsent: boolean;
  notificationConsent: boolean;
  autoPayConsent: boolean;
}

interface ApprovalStepProps {
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  onComplete?: (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => void;
}

// Configuration data for all checkboxes
const VERIFICATION_ITEMS: CheckboxItem[] = [
  {
    key: "dataConsent",
    id: "data-consent",
    icon: FileCheck,
    title: "Data Verification Consent",
    description:
      "I confirm that I have reviewed all the data and verify that the provided and modified information is accurate and complete.",
  },
  {
    key: "calculationConsent",
    id: "calculation-consent",
    icon: CheckCircle,
    title: "Bill Calculation Consent",
    description:
      "I have verified the bill calculation methodology used and confirm that all calculations are correct and in accordance with the applicable tariffs.",
  },
  {
    key: "notificationConsent",
    id: "notification-consent",
    icon: Mail,
    title: "Communication Consent",
    description:
      "I authorize the distribution of bills to consumers through all configured channels (email, SMS, and in-app notifications).",
  },
  {
    key: "autoPayConsent",
    id: "auto-pay-consent",
    icon: Lock,
    title: "Auto-Pay Authorization",
    description:
      "I authorize the automatic deduction of bill amounts from consumers who have opted for the auto-pay functionality.",
  },
  {
    key: "billingAccuracy",
    id: "billing-accuracy",
    icon: FileCheck,
    title: "Billing Data Accuracy",
    description:
      "I confirm that all billing data, including meter readings, tariffs, and service charges have been verified for accuracy.",
  },
  {
    key: "dataReviewed",
    id: "data-reviewed",
    icon: CheckCircle,
    title: "Consumer Data Reviewed",
    description:
      "I confirm that all consumer data is correct and any anomalies (high/low/zero consumption) have been reviewed and approved.",
  },
  {
    key: "approveDistribution",
    id: "approve-distribution",
    icon: Mail,
    title: "Approve Bill Distribution",
    description:
      "I approve the distribution of bills to consumers through configured channels (email, print, SMS, etc.).",
  },
  {
    key: "completeProcess",
    id: "complete-process",
    icon: Lock,
    title: "Complete Billing Process",
    description:
      "I authorize the completion of the billing cycle and understand that once approved, the bills will be finalized and cannot be modified.",
  },
];

// Reusable checkbox component
interface CheckboxItemProps {
  item: CheckboxItem;
  checked: boolean;
  onChange: (key: keyof VerificationChecks) => void;
}

const CheckboxItemComponent = ({
  item,
  checked,
  onChange,
}: CheckboxItemProps) => {
  const IconComponent = item.icon;

  return (
    <div className="flex items-start space-x-3">
      <Checkbox
        id={item.id}
        checked={checked}
        onCheckedChange={() => onChange(item.key)}
      />
      <div className="space-y-1">
        <Label
          htmlFor={item.id}
          className="font-medium text-base flex items-center"
        >
          <IconComponent className="h-5 w-5 mr-2 text-teal-600" />
          {item.title}
        </Label>
        <p className="text-sm text-muted-foreground">{item.description}</p>
      </div>
    </div>
  );
};

const ApprovalStep: React.FC<ApprovalStepProps> = ({
  onNext,
  onPrevious,
  currentStepIndex = 5,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
  onComplete,
}) => {
  const navigate = useNavigate();
  const { id, billRunId, action = "add" } = useParams();
  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(stepHelpers.getStepData(0)?.draftId || id);
  const { data: bulkBillData } = useBulkBillData(id);

  const [verificationChecks, setVerificationChecks] =
    useState<VerificationChecks>({
      billingAccuracy: false,
      dataReviewed: false,
      approveDistribution: false,
      completeProcess: false,
      dataConsent: false,
      calculationConsent: false,
      notificationConsent: false,
      autoPayConsent: bulkBillData?.autoPayConsent || false,
    });

  const [comments, setComments] = useState("");
  const [approverName, setApproverName] = useState("");
  const [approved, setApproved] = useState(false);
  const dataLoadedRef = useRef(false);

  // Helper functions for stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Get run_id from previous step (replaces params?.id)
  const getRunId = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getDataFromStep(3, "runId") || billRunId;
    } catch (error) {
      console.error("Failed to get run ID:", error);
      return null;
    }
  }, [stepHelpers]);

  const runId = getRunId();

  useEffect(() => {
    if (bulkBillData) {
      setVerificationChecks({
        billingAccuracy: bulkBillData?.billingAccuracy,
        dataReviewed: bulkBillData?.dataReviewed,
        approveDistribution: bulkBillData?.approveDistribution,
        completeProcess: bulkBillData?.completeProcess,
        dataConsent: bulkBillData?.dataConsent,
        calculationConsent: bulkBillData?.calculationConsent,
        notificationConsent: bulkBillData?.notificationConsent,
        autoPayConsent: bulkBillData?.autoPayConsent,
      });
      setComments(bulkBillData?.comments);
      setApproverName(bulkBillData?.approverName);
    }
  }, [bulkBillData]);

  const { mutateAsync: approveGenerateBill, isPending: isSubmitting } =
    useApproveGenerateBill(runId, stepHelpers.getStepData(0)?.draftId || id);

  // Load saved data from stepHelpers on component mount
  useEffect(() => {
    if (stepHelpers) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore state from saved data
          if (savedStepData.verificationChecks) {
            setVerificationChecks(savedStepData.verificationChecks);
          }
          if (savedStepData.comments) {
            setComments(savedStepData.comments);
          }
          if (savedStepData.approverName) {
            setApproverName(savedStepData.approverName);
          }
          if (savedStepData.approved !== undefined) {
            setApproved(savedStepData.approved);
          }
        }
      } catch (error) {
        console.error("Failed to load step data:", error);
      }
    }
  }, [stepHelpers, getStepData]);

  // Save state changes to stepHelpers
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const currentData = getStepData() || {};
        const updatedData = {
          ...currentData,
          verificationChecks,
          comments,
          approverName,
          approved,
          runId,
          lastUpdated: new Date().toISOString(),
        };
        saveStepData(updatedData);
        dataLoadedRef.current = true;
      } catch {
        dataLoadedRef.current = true;
      }
    }
  }, [
    verificationChecks,
    comments,
    approverName,
    approved,
    runId,
    saveStepData,
    getStepData,
    stepHelpers,
  ]);

  const handleCheckChange = (key: keyof VerificationChecks) => {
    setVerificationChecks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isAllChecked = Object.values(verificationChecks).every(Boolean);

  const handleDraft = async () => {
    try {
      if (clearValidationError) {
        clearValidationError();
      }

      const approvalData = {
        approverName,
        comments,
        approvalDate: new Date().toISOString(),
        verificationChecks,
        runId,
        billing_accuracy: verificationChecks.billingAccuracy,
        data_reviewed: verificationChecks.dataReviewed,
        approve_distribution: verificationChecks.approveDistribution,
        complete_process: verificationChecks.completeProcess,
        data_consent: verificationChecks.dataConsent,
        calculation_consent: verificationChecks.calculationConsent,
        notification_consent: verificationChecks.notificationConsent,
        auto_pay_consent: verificationChecks.autoPayConsent,
      };

      const finalStepData = {
        verificationChecks,
        comments,
        approverName,
        approved: true,
        approvalData,
        runId,
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      };

      const payload = {
        step: {
          step_working_on: 5,
          total_steps: 5,
        },
        bill_data: {
          billing_accuracy: verificationChecks.billingAccuracy,
          data_reviewed: verificationChecks.dataReviewed,
          approve_distribution: verificationChecks.approveDistribution,
          complete_process: verificationChecks.completeProcess,
          data_consent: verificationChecks.dataConsent,
          calculation_consent: verificationChecks.calculationConsent,
          notification_consent: verificationChecks.notificationConsent,
          auto_pay_consent: verificationChecks.autoPayConsent,
          comments: comments,
          approver_name: approverName,
        },
      };
      const response = await updateConsumer(payload);

      if (response) {
        saveStepData(finalStepData);
        navigate("/bx/history?tab=bulk&tab1=draft");
        await queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.bx.billHistory.BulkBillDraft(), 1],
          exact: false,
        });
      }
    } catch (error) {
      console.error("Approval failed:", error);
      if (setValidationError) {
        setValidationError("Failed to approve bills. Please try again.");
      }
    }
  };

  const handleApprove = async () => {
    try {
      if (clearValidationError) {
        clearValidationError();
      }

      const payload = {
        is_mail: "false",
        action: "approve",
        remote_utility_id: getRemoteUtilityId(),
      };

      await approveGenerateBill(payload);

      const approvalData = {
        approverName,
        comments,
        approvalDate: new Date().toISOString(),
        verificationChecks,
        runId,
      };

      setApproved(true);

      // Save final approval data
      const finalStepData = {
        verificationChecks,
        comments,
        approverName,
        approved: true,
        approvalData,
        runId,
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      };

      saveStepData(finalStepData);

      // Call workflow completion handler if provided
      if (onComplete && stepHelpers) {
        const allStepData = stepHelpers.getAllStepData();
        // Since this is the last step, all previous steps should be completed
        const completedSteps = [0, 1, 2, 3, 4, 5]; // All step indices
        const skippedSteps: number[] = []; // No skipped steps in this workflow

        onComplete(allStepData, completedSteps, skippedSteps);
      }
    } catch (error) {
      console.error("Approval failed:", error);
      if (setValidationError) {
        setValidationError("Failed to approve bills. Please try again.");
      }
    }
  };

  const handleComplete = () => {
    // Save final completion data
    const completionData = {
      verificationChecks,
      comments,
      approverName,
      approved,
      runId,
      workflowCompleted: true,
      completedAt: new Date().toISOString(),
    };

    saveStepData(completionData);

    // Navigate away from stepper (could be to a success page or bill search)
    // Since we can't use navigate directly, we'll use window.location
    window.location.href = `/bx/search?bill_run_id=${runId}`;
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="text-2xl font-bold text-primary">Bill Approval</h3>
        <p className="text-muted-foreground">
          Verify and approve bills for distribution
        </p>
      </div>


      {!approved ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Bill Verification Checklist</CardTitle>
              <CardDescription>
                Please verify the following items before approval
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {VERIFICATION_ITEMS.map((item) => (
                  <CheckboxItemComponent
                    key={item.key}
                    item={item}
                    checked={verificationChecks[item.key]}
                    onChange={handleCheckChange}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Approval Comments</CardTitle>
              <CardDescription>
                Add any comments or notes about this billing cycle
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Add any comments or special instructions regarding this billing cycle"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="min-h-[100px]"
              />

              <div className="space-y-2">
                <Label htmlFor="approver-name">Approver Name</Label>
                <Input
                  id="approver-name"
                  placeholder="Enter your full name"
                  value={approverName}
                  onChange={(e) => setApproverName(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Alert className={isAllChecked ? "border-success bg-success/10" : ""}>
            {isAllChecked ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <AlertTitle>
              {isAllChecked ? "Ready for Approval" : "Approval Requirements"}
            </AlertTitle>
            <AlertDescription>
              {isAllChecked
                ? "All verification items have been checked. You can now approve the bills."
                : "Please complete all verification items before approving the bills."}
            </AlertDescription>
          </Alert>

          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={onPrevious}
              disabled={isFirstStep}
            >
              Back to Summary
            </Button>
            <div className="flex gap-4">
              <Button onClick={handleDraft}>
                {isSubmitting ? (
                  <div className="flex items-center">
                    <span className="mr-2">Processing</span>
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  "Save As Draft"
                )}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!isAllChecked || !approverName || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <span className="mr-2">Processing</span>
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  "Approve & Finalize Bills"
                )}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <Card className="border-success">
            <CardHeader className="pb-4 text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-success" />
              </div>
              <CardTitle className="text-2xl text-success">
                Bills Approved Successfully
              </CardTitle>
              <CardDescription>
                The billing cycle has been approved and finalized
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-2">Approval Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Approved By</p>
                    <p className="font-medium">{approverName}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Approval Date</p>
                    <p className="font-medium">
                      {new Date().toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Approval Time</p>
                    <p className="font-medium">
                      {new Date().toLocaleTimeString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Run ID</p>
                    <p className="font-medium">{runId}</p>
                  </div>
                </div>
              </div>

              {comments && (
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    Approval Comments
                  </h4>
                  <p className="text-sm text-muted-foreground">{comments}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-center pt-4">
            <Button onClick={handleComplete}>Complete Process</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalStep;
